import React from 'react';
import styled from 'styled-components';
import { useSwipeable } from 'react-swipeable';
import { threeScreenLocales } from '../../locales/firstScreen';
import { Title } from '../UI/Title/Title';
import { Footer } from '../UI/Footer/Footer';

const LANG_KEY = 'stonpools-lang';

interface ThreeScreenProps {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  toHome?: () => void;
  handleDown?: () => void;
}

const Container = styled.div`
  background: #151515;
  min-height: 100vh;
  width: 100%;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const ContentWrapper = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: min(40px, 5vh) min(21px, 5vw);
  padding-bottom: 100px;
  box-sizing: border-box;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  margin-bottom: auto;
  padding-bottom: 100px;
`;

const Description = styled.p`
  color: #b4b4b4;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  text-align: center;
  margin: 0 auto;
  max-width: min(500px, 90%);
  line-height: 145%;
  letter-spacing: -0.01em;
  padding: 0 20px;
  white-space: pre-line;
`;

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: min(3vh, 30px) auto;
  flex: 1;
  justify-content: center;
`;

const StyledImage = styled.img`
  width: min(100%, 300px);
  height: auto;
  object-fit: contain;
`;

const HighlightBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: min(350px, 100%);
  min-height: 60px;
  margin: min(4vh, 30px) auto;
  padding: 10px;
`;

const HighlightText = styled.span`
  color: #0180ff;
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  font-weight: 700;
  text-align: center;
  width: 310px;
  line-height: 1.4;
  letter-spacing: -0.02em;
  white-space: pre-line;
  text-shadow:
    0 0 5px rgba(1, 128, 255, 0.4),
    0 0 10px rgba(1, 128, 255, 0.3),
    0 0 15px rgba(1, 128, 255, 0.2),
    0 0 20px rgba(1, 128, 255, 0.15),
    0 0 25px rgba(1, 128, 255, 0.1);
`;

const HomeButtonIcon = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
`;

const HomeButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  margin: 0;
  background-color: transparent;
`;

const ThreeScreen: React.FC<ThreeScreenProps> = ({
  onSwipeLeft,
  toHome,
  handleDown,
  onSwipeRight,
}) => {
  const handlers = useSwipeable({
    onSwipedLeft: () => onSwipeLeft?.(),
    onSwipedRight: () => onSwipeRight?.(),
    trackMouse: true,
    delta: 50,
    swipeDuration: 500,
  });

  const lang = (localStorage.getItem(LANG_KEY) as 'ru' | 'en') || 'ru';
  const t = threeScreenLocales[lang];

  return (
    <div {...handlers}>
      <Container>
        <ContentWrapper>
          <MainContent>
            <HomeButton onClick={toHome}>
              <HomeButtonIcon src="/assets/homeIcon.svg" />
            </HomeButton>
            <div style={{ width: '100%', textAlign: 'center' }}>
              <Title>{t.title}</Title>
              <Description>{t.description}</Description>
            </div>
            <ImageContainer>
              <StyledImage
                src="/assets/3screen.png"
                alt="Liquidity illustration"
              />
            </ImageContainer>
            <HighlightBox>
              <HighlightText>{t.highlight}</HighlightText>
            </HighlightBox>
          </MainContent>
          <Footer
            textRight={t.theory}
            onSwipeLeft={onSwipeLeft}
            handleDown={handleDown}
          ></Footer>
        </ContentWrapper>
      </Container>
    </div>
  );
};

export default ThreeScreen;
